// [목적] 모바일 /u/:handle — 아바타·통계 헤더 + 탭(내 폴리오/북마크/뱃지) + 콘텐츠
// [이유] W5-3(design-alignment): 탭 색 토큰 정렬(BRAND 활성 + INK_LOW 카운트 표기). 시안 정합.
//         W2(design-alignment): 쿨톤 하드코딩 → paper/ink/line 정본 토큰 교체.
//         fe-be-gap-tier1 T14(ADR-T1-5): BADGES·LOCKED mock 제거 → getMyBadges 실데이터.
//         보유 뱃지만 렌더(미해금/잠금 표현 없음 — ADR-T1-5). badge.kind → emoji 로컬 매핑.
//         fe-mobile-live-data: ME/MyFolioMock(design-mock) 제거 — usePublicProfile(handle)/
//           useUserFolios(handle) 실데이터로 교체. 어떤 /u/:handle 로 진입해도 해당 사용자의 실제
//           프로필이 표시되도록 handle prop 을 ProfilePage.tsx 로부터 전달받는다(기존 버그 수정).
//           isMe=false(타인 프로필) 이면 편집/설정/새폴리오 버튼 대신 팔로우 버튼(useFollowToggle)을 노출.
// [사이드 이팩트] BADGES_MOCK·LOCKED·ME·MyFolioMock 상수 제거. badgeCount 는 getMyBadges 응답 length.
//                 [G2-3, 2026-09-17] 북마크 탭 mock(BOOKMARKS 배열) 제거 → getMyBookmarks(계약 C3)
//                 useInfiniteQuery 실데이터로 교체(isMe=true 일 때만 조회 — JwtAuthGuard 전용 API, liked/forked 와 동일 관례).
//                 badges 탭은 isMe=true 일 때만 조회(getMyBadges 는 JwtAuthGuard 필수 — 본인 전용 API).
// [수정 시 고려사항] pages/auth/** 쿨톤은 의도된 전용 톤(ADR-DA6) — 절대 변경 금지.
//                   ProfileMobile 탭: folios/bookmarks/liked/forks/badges — liked·forks 는 데스크톱(ProfilePage)과 같은 API(GET /users/me/liked·/forked)를 쓰는 본인 전용 탭.
//                   handle 이 undefined 이면 모든 조회 훅이 enabled=false 로 대기(ProfilePage 라우트 보장상 발생 안 함).
//                   [G1-2, 2026-09-17] usePublicProfile isError 시 무한 스피너 대신 InlineErrorState(재시도).
//                   M7 P2: 팔로워/팔로잉 통계 클릭 → /u/:handle/followers·following 이동. isPrivate=true 이면
//                   탭 숨기고 "비공개 프로필입니다" 안내만 표시.
//                   FlowLine B6: 타인 프로필 "···" 메뉴 → ReportDialog(targetType='user') 재사용(데스크톱 동일 관례).

import {
  Box,
  ButtonBase,
  CircularProgress,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import type { FolioList } from '@gangwoncanvas/types';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import {
  getMyBadgeCatalog,
  getMyBookmarks,
  getMyForked,
  getMyLiked,
} from '../../api/user.client.js';
import { queryKeys } from '../../api/queryKeys.js';
import { BadgeCollection } from '../../components/badges/BadgeCollection.js';
import { usePublicProfile, useUserFolios } from '../../hooks/useUserProfile.js';
import { useFollowToggle } from '../../hooks/useFollowToggle.js';
import { useAuthGate } from '../../hooks/useAuthGate.js';
import { InlineErrorState } from '../../components/feedback/InlineErrorState.js';
import { LoginPromptModal } from '../../components/auth/LoginPromptModal.js';
import { ReportDialog } from '../../components/report/ReportDialog.js';

// [목적] paper/ink/line 정본 토큰 바인딩 — W2(design-alignment) 쿨톤 정본화
// [이유] ADR-TR8: c.text.*(쿨톤) → c.ink.*(warm), 하드코딩 hex → 토큰 참조. [ADR-TR8 예외는 2026-09-18 폐기 — ink.low(#6F6A5C) 는 AA 충족이라 텍스트 사용 가능]
//         ink.low 는 본문 미사용 보조 텍스트(비공개 상태 텍스트, 뱃지 설명 등) 한정.
// [사이드 이팩트] 배경 #F6F8FB → paper[1] #FAF9F7, 텍스트 계열 warm 전환
// [수정 시 고려사항] contrast.spec.ts 에서 paper[1] 위 ink.high/mid 4.5:1 이상 게이트 검증됨.
const c = tokens.color;
const L = 'light' as const;
const TEXT_HI = c.ink.high[L]; // #29261B (← 쿨톤 c.text.high #14181F)
const TEXT_MID = c.ink.mid[L]; // #5C5849 (← 쿨톤 c.text.mid #586069)
const TEXT_LOW = c.ink.low[L]; // #6F6A5C — 보조 텍스트 한정(ADR-TR8, 본문 미사용)
const BORDER = c.line.base[L]; // #E8E2D5 (← #E1E7EF)
const BG1 = c.paper[1][L]; // #FAF9F7 (← #F6F8FB)
const BRAND = c.brand[500][L];
const GREEN = c.accent[500][L];

type TabId = 'folios' | 'bookmarks' | 'liked' | 'forks' | 'badges';

const TABS: ReadonlyArray<{ id: TabId; label: string }> = [
  { id: 'folios', label: '내 폴리오' },
  { id: 'bookmarks', label: '북마크' },
  { id: 'liked', label: '좋아요' },
  { id: 'forks', label: 'Fork' },
  { id: 'badges', label: '뱃지' },
];

/**
 * [목적] 폴리오 행 — 실데이터(UserFolioSnapshot) 기반
 * [이유] fe-mobile-live-data — MyFolioMock 목업(color/emoji/pub) 제거, 실 스냅샷 필드로 렌더.
 * [사이드 이팩트] 없음
 * [수정 시 고려사항] coverImageUrl 없으면 이모지 폴백(🗺).
 */
function MobileFolioRow({
  f,
  onClick,
}: {
  f: {
    id: string;
    title: string;
    coverImageUrl: string | null;
    likeCount: number;
    visibility: 'public' | 'link' | 'private';
  };
  onClick: () => void;
}): JSX.Element {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`내 폴리오 ${f.title}`}
      sx={{
        display: 'flex',
        gap: '10px',
        bgcolor: c.paper[0][L],
        borderRadius: '12px',
        overflow: 'hidden',
        border: `1px solid ${BORDER}`,
        boxShadow: '0 1px 3px rgba(41,38,27,.05)',
        width: '100%',
        textAlign: 'left',
      }}
    >
      <Box
        sx={{
          width: 64,
          bgcolor: c.paper[2][L],
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 28,
          alignSelf: 'stretch',
          overflow: 'hidden',
        }}
        aria-hidden="true"
      >
        {f.coverImageUrl ? (
          <Box
            component="img"
            src={f.coverImageUrl}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          '🗺'
        )}
      </Box>
      <Box sx={{ flex: 1, p: '10px 12px 10px 0' }}>
        <Typography
          sx={{ fontSize: 12, fontWeight: 600, color: TEXT_HI, mb: '4px', lineHeight: 1.35 }}
        >
          {f.title}
        </Typography>
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Box component="span" sx={{ fontSize: 10, color: TEXT_MID }}>
            ♥ {f.likeCount}
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: 9,
              borderRadius: '5px',
              px: '6px',
              py: '1px',
              fontWeight: 600,
              bgcolor: f.visibility === 'public' ? '#E8F8EF' : BG1,
              color: f.visibility === 'public' ? GREEN : TEXT_LOW,
            }}
          >
            {f.visibility === 'public' ? '공개' : f.visibility === 'link' ? '링크 공개' : '비공개'}
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', pr: '12px' }}>
        <Box component="span" sx={{ fontSize: 16, color: TEXT_LOW }}>
          ›
        </Box>
      </Box>
    </ButtonBase>
  );
}

/**
 * [목적] 본인 폴리오 목록 탭(북마크/좋아요/Fork) 공용 무한스크롤 훅
 * [이유] 세 탭이 같은 useInfiniteQuery 패턴(커서 페이징·staleTime 2분)이라 복붙하지 않고 한 곳에 둔다.
 * [사이드 이팩트] enabled=false(타인 프로필)면 조회하지 않고 빈 목록을 돌려준다(JwtAuthGuard 전용 API — 불필요한 401 방지).
 * [수정 시 고려사항] 커서 포맷은 API 별로 호환되지 않으므로(북마크 전용 등) 탭마다 queryKey 를 분리해서 넘긴다.
 */
function useMyFolioList(
  queryKey: readonly unknown[],
  fetchPage: (p: { cursor?: string; limit: number }) => Promise<FolioList>,
  enabled: boolean,
) {
  const q = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage({ cursor: pageParam as string | undefined, limit: 20 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
    staleTime: 1000 * 60 * 2,
    throwOnError: false,
  });
  return {
    items: enabled ? (q.data?.pages.flatMap((p) => p.items) ?? []) : [],
    isLoading: q.isLoading,
    hasNext: Boolean(q.hasNextPage),
    fetchNext: () => void q.fetchNextPage(),
    isFetchingNext: q.isFetchingNextPage,
  };
}

/**
 * [목적] 북마크/좋아요/Fork 탭 본문 — 로딩·빈 상태·행 목록·'더 보기'
 * [이유] 세 탭의 마크업이 라벨 문구만 다르고 동일 — 한 컴포넌트로 통합.
 * [사이드 이팩트] 없음(표시 전용). 행 클릭 시 /folio/:id 로 이동.
 * [수정 시 고려사항] isMe=false 면 조회 자체를 하지 않으므로 lockedText 안내만 표시한다.
 */
function MobileFolioListTab({
  list,
  isMe,
  name,
  emptyText,
  lockedText,
  onOpen,
}: {
  list: ReturnType<typeof useMyFolioList>;
  isMe: boolean;
  name: string;
  emptyText: string;
  lockedText: string;
  onOpen: (id: string) => void;
}): JSX.Element {
  if (!isMe) {
    return (
      <Box sx={{ textAlign: 'center', py: '32px', color: TEXT_LOW }}>
        <Typography sx={{ fontSize: 12, color: TEXT_MID }}>{lockedText}</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {list.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: '24px' }}>
          <CircularProgress size={22} aria-label={`${name} 불러오는 중`} />
        </Box>
      )}
      {!list.isLoading && list.items.length === 0 && (
        <Box sx={{ textAlign: 'center', py: '24px', color: TEXT_LOW }}>
          <Typography sx={{ fontSize: 12, color: TEXT_MID }}>{emptyText}</Typography>
        </Box>
      )}
      {!list.isLoading &&
        list.items.map((f) => <MobileFolioRow key={f.id} f={f} onClick={() => onOpen(f.id)} />)}
      {list.hasNext && (
        <Box sx={{ textAlign: 'center', pt: '4px' }}>
          <ButtonBase
            onClick={list.fetchNext}
            disabled={list.isFetchingNext}
            aria-label={`${name} 더 불러오기`}
            sx={{
              px: '20px',
              py: '8px',
              fontSize: 12,
              color: BRAND,
              border: `1px solid ${BORDER}`,
              borderRadius: `${tokens.radius.m}px`,
              '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
            }}
          >
            {list.isFetchingNext ? (
              <CircularProgress size={14} color="inherit" aria-label="불러오는 중" />
            ) : (
              '더 보기'
            )}
          </ButtonBase>
        </Box>
      )}
    </Box>
  );
}

export function ProfileMobile({ handle }: { handle: string | undefined }): JSX.Element {
  const navigate = useNavigate();
  // [2026-09-21 notif-badge-deeplink] ?tab=badges 등 쿼리스트링으로 초기 탭 진입 지원(데스크톡 ProfilePage.tsx 와 동일 관례).
  const [searchParams] = useSearchParams();
  const initialTabParam = searchParams.get('tab');
  const [tab, setTab] = useState<TabId>(
    TABS.some((t) => t.id === initialTabParam) ? (initialTabParam as TabId) : 'folios',
  );

  // fe-mobile-live-data: ME mock 제거 — usePublicProfile(handle) 실데이터 (ProfilePage.tsx 데스크톱과 동일 훅)
  const {
    profile,
    isLoading: profileLoading,
    isError: profileError,
    refetch: refetchProfile,
  } = usePublicProfile(handle);
  const { folios, isLoading: foliosLoading } = useUserFolios(handle);
  const isMe = profile?.isMe === true;
  const { toggle: toggleFollow, isPending: followPending } = useFollowToggle(handle ?? '');

  // FlowLine B6: 타인 프로필 "···" 메뉴 → 신고(ReportDialog targetType='user' 재사용, 데스크톱과 동일 관례)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const reportGate = useAuthGate('report.create');

  // [목적] 뱃지 도감 실데이터 — T14 ADR-T1-5 + 2026-09-21 badge-catalog(잠금 슬롯 포함).
  // [이유] 보유 목록만으로는 미획득 배지를 그릴 수 없다. 카탈로그 1회 조회로 획득/미획득/진행률을 모두 받는다.
  //        데스크톱 ProfilePage 와 같은 쿼리 키라 캐시를 공유한다.
  // [사이드 이팩트] 뱃지 탭 count 는 '획득' 개수(earnedCount).
  // [수정 시 고려사항] 타인 프로필(isMe=false)은 조회 자체를 하지 않음(불필요한 401 방지).
  const { data: badgeCatalog } = useQuery({
    queryKey: queryKeys.user.badgeCatalog('me'),
    queryFn: () => getMyBadgeCatalog(),
    enabled: isMe,
    staleTime: 1000 * 60 * 5,
    throwOnError: false,
  });
  const badgeEarnedCount = isMe ? (badgeCatalog?.earnedCount ?? 0) : 0;

  // [목적] 본인 북마크/좋아요/Fork 목록 실데이터 무한스크롤 — isMe 일 때만 조회(JwtAuthGuard 전용 API).
  // [이유] G2-3(계약 C3) 북마크 + 데스크톱(ProfilePage)과 같은 liked/forked API 를 모바일에도 제공. 탭 전환 시 캐시 유지(staleTime 2분).
  // [수정 시 고려사항] 북마크 커서는 bookmarked 전용(liked/forked 와 호환 불가, BE 주석 근거) — 탭마다 별도 queryKey.
  const bookmarks = useMyFolioList(queryKeys.user.bookmarks('me'), getMyBookmarks, isMe);
  const liked = useMyFolioList(queryKeys.user.liked('me'), getMyLiked, isMe);
  const forked = useMyFolioList(queryKeys.user.forked('me'), getMyForked, isMe);

  // [목적] G1-2: usePublicProfile 실패 시 무한 로딩 스피너 대신 InlineErrorState(재시도) 렌더
  // [이유] 기존엔 isLoading=false + profile=undefined(=error)를 "로딩 중"으로 오인해 스피너가 영구 표시됨.
  // [사이드 이팩트] 없음(표시 전용 분기 추가).
  if (profileError) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: BG1,
        }}
      >
        <InlineErrorState message="프로필을 불러오지 못했습니다." onRetry={refetchProfile} />
      </Box>
    );
  }

  if (profileLoading || !profile) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: BG1,
        }}
      >
        <CircularProgress size={28} aria-label="프로필 불러오는 중" />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: BG1 }}>
      {/* 프로필 헤더 */}
      <Box sx={{ bgcolor: c.paper[0][L], p: '20px 20px 0', borderBottom: `1px solid ${BORDER}` }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '14px', mb: '16px' }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              flexShrink: 0,
              background: 'linear-gradient(135deg, #1F6FEB, #2BB673)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 24,
              fontWeight: 700,
              overflow: 'hidden',
            }}
          >
            {profile.avatarUrl ? (
              <Box
                component="img"
                src={profile.avatarUrl}
                alt={`${profile.displayName} 프로필 사진`}
                loading="lazy"
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              profile.displayName.charAt(0).toUpperCase()
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: TEXT_HI, mb: '2px' }}>
              {profile.displayName}
            </Typography>
            <Typography sx={{ fontSize: 12, color: TEXT_MID, mb: '8px' }}>
              @{profile.handle}
            </Typography>
            <Box sx={{ display: 'flex', gap: '16px' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: TEXT_HI,
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                  }}
                >
                  {profile.folioCount}
                </Typography>
                <Typography sx={{ fontSize: 10, color: TEXT_MID }}>폴리오</Typography>
              </Box>
              {/* M7 P2: 팔로워/팔로잉 카운트 클릭 → /u/:handle/followers·following(FollowListPage.tsx) */}
              <ButtonBase
                onClick={() => navigate(`/u/${profile.handle}/followers`)}
                aria-label={`팔로워 ${profile.followerCount}명`}
                sx={{
                  textAlign: 'center',
                  flexDirection: 'column',
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: TEXT_HI,
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                  }}
                >
                  {profile.followerCount}
                </Typography>
                <Typography sx={{ fontSize: 10, color: TEXT_MID }}>팔로워</Typography>
              </ButtonBase>
              <ButtonBase
                onClick={() => navigate(`/u/${profile.handle}/following`)}
                aria-label={`팔로잉 ${profile.followingCount}명`}
                sx={{
                  textAlign: 'center',
                  flexDirection: 'column',
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: TEXT_HI,
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                  }}
                >
                  {profile.followingCount}
                </Typography>
                <Typography sx={{ fontSize: 10, color: TEXT_MID }}>팔로잉</Typography>
              </ButtonBase>
            </Box>
          </Box>
          {isMe ? (
            <Stack direction="column" spacing="6px" sx={{ flexShrink: 0 }}>
              <ButtonBase
                onClick={() => navigate('/settings/profile')}
                aria-label="프로필 편집"
                sx={{
                  border: `1px solid ${BORDER}`,
                  bgcolor: c.paper[0][L],
                  borderRadius: '8px',
                  px: '12px',
                  py: '6px',
                  fontSize: 12,
                  color: TEXT_MID,
                }}
              >
                편집
              </ButtonBase>
              <ButtonBase
                onClick={() => navigate('/settings')}
                aria-label="설정"
                sx={{
                  border: `1px solid ${BORDER}`,
                  bgcolor: c.paper[0][L],
                  borderRadius: '8px',
                  px: '12px',
                  py: '6px',
                  fontSize: 12,
                  color: TEXT_MID,
                }}
              >
                ⚙
              </ButtonBase>
            </Stack>
          ) : (
            <Stack direction="row" spacing="6px" sx={{ flexShrink: 0 }}>
              <ButtonBase
                onClick={() => toggleFollow(profile.isFollowing)}
                disabled={followPending}
                aria-label={profile.isFollowing ? '팔로우 취소' : `${profile.displayName} 팔로우`}
                aria-pressed={profile.isFollowing}
                sx={{
                  bgcolor: profile.isFollowing ? c.paper[0][L] : BRAND,
                  color: profile.isFollowing ? TEXT_HI : '#fff',
                  border: `1px solid ${profile.isFollowing ? BORDER : BRAND}`,
                  borderRadius: '8px',
                  px: '14px',
                  py: '7px',
                  fontSize: 12,
                  fontWeight: 700,
                  opacity: followPending ? 0.7 : 1,
                }}
              >
                {followPending ? (
                  <CircularProgress size={12} color="inherit" />
                ) : profile.isFollowing ? (
                  '팔로우 중'
                ) : (
                  '팔로우'
                )}
              </ButtonBase>
              {/* FlowLine B6: "···" 메뉴 → 신고 */}
              <ButtonBase
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                aria-label="더 보기"
                aria-haspopup="menu"
                aria-expanded={Boolean(menuAnchor)}
                sx={{
                  border: `1px solid ${BORDER}`,
                  bgcolor: c.paper[0][L],
                  borderRadius: '8px',
                  px: '10px',
                  py: '7px',
                  fontSize: 13,
                  color: TEXT_HI,
                }}
              >
                ···
              </ButtonBase>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
              >
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    reportGate.guard(() => setReportOpen(true));
                  }}
                >
                  신고
                </MenuItem>
              </Menu>
              <LoginPromptModal
                open={reportGate.isPromptOpen}
                intent={reportGate.pendingIntent}
                onClose={reportGate.closePrompt}
              />
              {reportOpen && (
                <ReportDialog
                  targetType="user"
                  targetId={profile.id}
                  open
                  onClose={() => setReportOpen(false)}
                />
              )}
            </Stack>
          )}
        </Box>

        {profile.bio && (
          <Typography sx={{ fontSize: 12, color: TEXT_MID, mb: '14px', lineHeight: 1.5 }}>
            {profile.bio}
          </Typography>
        )}

        {/* M7 P2: 비공개 프로필(isPrivate=true) — 탭 자체를 숨기고 안내만 표시 */}
        {profile.isPrivate && (
          <Box sx={{ textAlign: 'center', py: '24px', color: TEXT_LOW }}>
            <Box aria-hidden="true" sx={{ fontSize: 28, mb: '8px', opacity: 0.4 }}>
              🔒
            </Box>
            <Typography sx={{ fontSize: 12, color: TEXT_MID }}>
              @{profile.handle}님이 프로필을 비공개로 설정했습니다.
            </Typography>
          </Box>
        )}

        {/* 탭 — W5-3 색 토큰/카운트 정렬 */}
        {!profile.isPrivate && (
          <>
            <Box
              role="tablist"
              aria-label="프로필 탭"
              sx={{ display: 'flex', overflowX: 'auto', borderBottom: `1px solid ${BORDER}` }}
            >
              {TABS.map((t) => {
                const active = tab === t.id;
                // [목적] 탭 카운트 — 폴리오/북마크/뱃지 모두 실값(G2-3: 북마크도 mock 제거 완료)
                const count =
                  t.id === 'folios'
                    ? (folios?.items.length ?? 0)
                    : t.id === 'bookmarks'
                      ? bookmarks.items.length
                      : t.id === 'liked'
                        ? liked.items.length
                        : t.id === 'forks'
                          ? forked.items.length
                          : badgeEarnedCount;
                return (
                  <ButtonBase
                    key={t.id}
                    role="tab"
                    aria-selected={active}
                    aria-controls={`tabpanel-mobile-${t.id}`}
                    id={`tab-mobile-${t.id}`}
                    onClick={() => setTab(t.id)}
                    aria-label={`${t.label} ${count}개`}
                    sx={{
                      flex: '1 0 auto',
                      p: '10px 10px',
                      fontSize: 12,
                      fontWeight: active ? 700 : 400,
                      color: active ? BRAND : TEXT_MID,
                      borderBottom: `2px solid ${active ? BRAND : 'transparent'}`,
                      mb: '-1px',
                      '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                      display: 'flex',
                      gap: '3px',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {t.label}
                    <Box
                      component="span"
                      sx={{
                        fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                        fontSize: 10,
                        color: TEXT_LOW,
                        ml: '2px',
                      }}
                    >
                      {count}
                    </Box>
                  </ButtonBase>
                );
              })}
            </Box>
          </>
        )}
      </Box>

      {/* 콘텐츠 — 비공개 프로필이면 렌더하지 않음(위 안내 UI 로 대체) */}
      {!profile.isPrivate && (
        <Box
          id={`tabpanel-mobile-${tab}`}
          role="tabpanel"
          aria-labelledby={`tab-mobile-${tab}`}
          sx={{ p: '16px 20px 32px' }}
        >
          {tab === 'folios' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {foliosLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: '24px' }}>
                  <CircularProgress size={22} aria-label="폴리오 불러오는 중" />
                </Box>
              )}
              {!foliosLoading && (folios?.items.length ?? 0) === 0 && (
                <Box sx={{ textAlign: 'center', py: '24px', color: TEXT_LOW }}>
                  <Typography sx={{ fontSize: 12, color: TEXT_MID }}>
                    아직 폴리오가 없습니다.
                  </Typography>
                </Box>
              )}
              {!foliosLoading &&
                folios?.items.map((f) => (
                  <MobileFolioRow key={f.id} f={f} onClick={() => navigate(`/folio/${f.id}`)} />
                ))}
              {isMe && (
                <ButtonBase
                  onClick={() => navigate('/chat')}
                  aria-label="새 폴리오 만들기"
                  sx={{
                    mt: '4px',
                    p: '12px',
                    bgcolor: '#fff',
                    border: `1px dashed ${BORDER}`,
                    borderRadius: '12px',
                    fontSize: 13,
                    color: BRAND,
                    fontWeight: 500,
                  }}
                >
                  + 새 폴리오 만들기
                </ButtonBase>
              )}
            </Box>
          )}

          {tab === 'bookmarks' && (
            <MobileFolioListTab
              list={bookmarks}
              isMe={isMe}
              name="북마크"
              emptyText="아직 북마크한 폴리오가 없습니다."
              lockedText="북마크는 본인 프로필에서만 확인 가능합니다."
              onOpen={(id) => navigate(`/folio/${id}`)}
            />
          )}

          {tab === 'liked' && (
            <MobileFolioListTab
              list={liked}
              isMe={isMe}
              name="좋아요"
              emptyText="아직 좋아요한 폴리오가 없습니다."
              lockedText="좋아요는 본인 프로필에서만 확인 가능합니다."
              onOpen={(id) => navigate(`/folio/${id}`)}
            />
          )}

          {tab === 'forks' && (
            <MobileFolioListTab
              list={forked}
              isMe={isMe}
              name="Fork"
              emptyText="아직 Fork한 폴리오가 없습니다."
              lockedText="Fork 목록은 본인 프로필에서만 확인 가능합니다."
              onOpen={(id) => navigate(`/folio/${id}`)}
            />
          )}

          {tab === 'badges' && (
            <Box>
              {!isMe ? (
                <Box sx={{ textAlign: 'center', py: '32px', color: TEXT_LOW }}>
                  <Typography sx={{ fontSize: 12, color: TEXT_MID }}>
                    뱃지는 본인 프로필에서만 확인 가능합니다.
                  </Typography>
                </Box>
              ) : !badgeCatalog ? (
                <Box sx={{ textAlign: 'center', py: '32px' }}>
                  <Typography sx={{ fontSize: 12, color: TEXT_MID }}>
                    뱃지를 불러오는 중이에요…
                  </Typography>
                </Box>
              ) : (
                /* [목적] 뱃지 도감 — 데스크톱 프로필과 같은 BadgeCollection 공용(2026-09-21 badge-catalog) */
                <BadgeCollection
                  items={badgeCatalog.items}
                  earnedCount={badgeCatalog.earnedCount}
                  totalCount={badgeCatalog.totalCount}
                  minTileWidth={100}
                />
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
